import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Wrench, Car, Sparkles, Phone, MessageCircle, Construction, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_47615226-8be8-492e-9b64-c74bd12fbe55/artifacts/a0f8zaxp_02_MA_Africa_Detailing_Full_Colour_Transparent.webp";

const rooms = [
  { id: 'fix', title: 'FIX', subtitle: 'Mechanical', path: '/fix', icon: Wrench },
  { id: 'restore', title: 'RESTORE', subtitle: 'Panel Beating', path: '/restore', icon: Car },
  { id: 'detail', title: 'DETAIL', subtitle: 'Detailing', path: '/detail', icon: Sparkles },
];

function RoomSwitcher({ currentRoom }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const currentRoomData = rooms.find(r => r.id === currentRoom);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
      >
        {currentRoomData && <currentRoomData.icon className="w-4 h-4 text-[#C41E3A]" />}
        <span className="text-sm font-medium text-white">{currentRoomData?.title}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-2 w-48 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl overflow-hidden z-50"
          >
            {rooms.filter(r => r.id !== currentRoom).map((room) => (
              <button
                key={room.id}
                onClick={() => {
                  navigate(room.path);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors"
              >
                <room.icon className="w-4 h-4 text-zinc-400" />
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{room.title}</p>
                  <p className="text-xs text-zinc-500">{room.subtitle}</p>
                </div>
              </button>
            ))}
            <div className="border-t border-zinc-800">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-xs text-zinc-500"
                onClick={() => setIsOpen(false)}
              >
                ← Back to all services
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FixRoom() {
  const whatsappNumber = '27760784050';

  return (
    <main data-testid="fix-room" className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/">
              <img src={LOGO_URL} alt="MA Africa" className="h-8 md:h-10" />
            </Link>
            <RoomSwitcher currentRoom="fix" />
          </div>
          
          <div className="flex items-center gap-3">
            <a
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi MA Africa, I need help with a mechanical issue')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Coming Soon Content */}
      <section className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-[600px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Construction Icon */}
            <div className="relative inline-block mb-8">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-3xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center border border-amber-500/30">
                <Construction className="w-12 h-12 md:w-16 md:h-16 text-amber-500" />
              </div>
              <motion.div
                className="absolute -top-2 -right-2 w-8 h-8 bg-[#C41E3A] rounded-full flex items-center justify-center"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <Wrench className="w-4 h-4 text-white" />
              </motion.div>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              <span className="text-xs text-amber-400 uppercase tracking-wider font-medium">Under Construction</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              FIX
            </h1>
            <p className="text-lg text-zinc-400 mb-2">
              Mechanical Services
            </p>
            <p className="text-sm text-zinc-500 max-w-md mx-auto mb-8">
              We're building something great! Our online mechanical booking system is coming soon. In the meantime, contact us directly for all mechanical services.
            </p>

            {/* Services Preview */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-8">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-4">Coming Soon</p>
              <div className="flex flex-wrap justify-center gap-2">
                {['Diagnostics', 'Servicing', 'Engine Repairs', 'Brakes', 'Suspension', 'Electrical'].map((service) => (
                  <span key={service} className="px-3 py-1.5 bg-zinc-800 text-zinc-400 text-xs rounded-full">
                    {service}
                  </span>
                ))}
              </div>
            </div>

            {/* Contact Options */}
            <p className="text-sm text-zinc-400 mb-4">
              Need mechanical assistance now?
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <a
                href="tel:0760784050"
                className="bg-[#C41E3A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                076 078 4050
              </a>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi MA Africa, I need mechanical assistance')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#20bd5a] transition-colors inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </a>
            </div>

            {/* Back Link */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 border-t border-zinc-900 py-6 px-6">
        <div className="max-w-[1100px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">
            © {new Date().getFullYear()} MA Africa Fix & Detail
          </p>
          <p className="text-xs text-zinc-600">
            FIX. RESTORE. DETAIL.
          </p>
        </div>
      </footer>
    </main>
  );
}
